import React from 'react';

import { EVENT_TYPE, type AnalyticsEventPayload } from '@atlaskit/editor-common/analytics';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPlugin, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';
import { isResolvingMentionProvider } from '@atlaskit/mention/resource';
import { isPromise } from '@atlaskit/mention/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { AIProactivePlugin } from './aiProactivePluginType';
import { createProactiveAIPlugin } from './pm-plugins/ai-proactive/ai-proactive-plugin';
import { aiProactivePluginKey } from './pm-plugins/ai-proactive/ai-proactive-plugin-key';
import {
	getRecommendationByFilters,
	getUnreadRecommendationsCount,
} from './pm-plugins/ai-proactive/utils';
import { deriveProactiveAIApiUrl } from './pm-plugins/ai-proactive/utils/url';
import type { AIProactivePluginSharedState } from './types';
import { ContentComponentWrapper } from './ui/components/ContentComponent/ContentComponentWrapper';
import {
	createToggleSuggestionsPanel,
	getContextPanel,
} from './ui/components/context-panel/context-panel';
import {
	getAIPrimaryToolbarComponent,
	getAIPrimaryToolbarComponentOld,
} from './ui/toolbars/primary-toolbar';

export const aiProactivePlugin: AIProactivePlugin = ({ api, config }) => {
	//Note: This is a hack to get the editor view reference in the plugin. Copied from table plugin.
	//This is needed to get the current selection in the editor
	const editorViewRef: Record<'current', EditorView | undefined> = { current: undefined };

	/**
	 * Helper function to retrieve the name details for a mention from the mentionProvider
	 */
	const getMentionNameDetails = async (id: string) => {
		const { mentionProvider } = api?.mention?.sharedState.currentState() || {};

		if (!mentionProvider || !isResolvingMentionProvider(mentionProvider)) {
			return;
		}

		const nameDetails = mentionProvider.resolveMentionName(id);

		if (isPromise(nameDetails)) {
			const mention = await nameDetails;
			return mention;
		}

		return nameDetails;
	};

	const { product, aiGlobalOptIn } = config;

	const aiOptIn = aiGlobalOptIn || {
		status: 'enabled',
		triggerOptInFlow: () => {},
	};

	api?.primaryToolbar?.actions.registerComponent({
		name: 'aiExperience',
		component: fg('platform_editor_ai_object_sidebar_injection')
			? getAIPrimaryToolbarComponent({
					api,
					aiGlobalOptIn: aiOptIn,
					toggleSuggestionsPanel: createToggleSuggestionsPanel(api, config, editorViewRef),
				})
			: getAIPrimaryToolbarComponentOld({ api, aiGlobalOptIn: aiOptIn }),
	});

	return {
		name: 'aiProactive',
		pmPlugins: () => {
			const plugins: PMPlugin[] = [
				{
					name: 'aiProactiveEditorViewRef',
					plugin: () => {
						return new SafePlugin({
							view: (editorView) => {
								// Do not cleanup the editorViewRef on destroy
								// because some functions may point to a stale
								// reference and this means we will return null.
								// EditorView is assumed to be stable so we do not need to
								// cleanup.
								editorViewRef.current = editorView;
								return {};
							},
						});
					},
				},
				{
					name: 'ai-proactive-plugin',
					plugin: ({ dispatch, getIntl, dispatchAnalyticsEvent }: PMPluginFactoryParams) => {
						// Ignored via go/ees007
						// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
						// FIXME fix the analytics event payload
						const payload = {
							action: 'init',
							actionSubject: 'editor',
							actionSubjectId: 'editorPluginAIProactive',
							eventType: EVENT_TYPE.TRACK,
							attributes: {
								aiGlobalOptIn: aiOptIn?.status,
							},
						} as unknown as AnalyticsEventPayload;
						dispatchAnalyticsEvent(payload);

						return createProactiveAIPlugin({
							dispatch,
							getIntl,
							proactiveAIConfig: {
								enabled: true,
								defaultToggledState: false,
								apiUrl: deriveProactiveAIApiUrl(),
								timings: {
									// disabling the current chunk checking
									currentChunks: -1,
									currentChunksMaxWait: 30000,
									nonCurrentChunks: 5000,
								},
								documentChecker: {
									enabled: true,
									blocksPerRequest: 7,
									timings: {
										delayBetweenChecks: 0,
										numberOfRequestsForIncrease: 2,
										delayIncrease: 0,
										maxDelay: 0,
									},
								},
							},
							product: product,
							getMentionNameDetails,
							api,
						});
					},
				},
			];

			return plugins;
		},
		getSharedState(state: EditorState | undefined): AIProactivePluginSharedState {
			if (!state) {
				return {
					isProactiveEnabled: false,
					recommendations: [],
					displayAllSuggestions: false,
					isLoading: false,
					isEmptyDocument: false,
					hasNoMoreSuggestions: true,
					displayAllPanelSuggestions: false,
					isProactiveContextPanelOpen: false,
					showNewRecommendationsNotification: false,
					unreadRecommendationCount: 0,
					decorationSet: undefined,
				};
			}

			const proactivePluginState = aiProactivePluginKey.getState(state) ?? {};
			const filteredRecommendations = getRecommendationByFilters(proactivePluginState) ?? [];

			return {
				isProactiveEnabled: proactivePluginState.isProactiveEnabled ?? false,
				recommendations: filteredRecommendations,
				selectedRecommendationId: proactivePluginState.selectedRecommendationId,
				hoveredRecommendationId: proactivePluginState.hoveredRecommendationId,
				isLoading: proactivePluginState.isLoading,
				isEmptyDocument: isEmptyDocument(state.doc),
				displayAllSuggestions: proactivePluginState?.settings?.displayAllSuggestions ?? false,
				displayAllPanelSuggestions:
					proactivePluginState?.settings?.displayAllPanelSuggestions ?? false,
				hasNoMoreSuggestions: proactivePluginState.hasNoMoreSuggestions,
				isProactiveContextPanelOpen: proactivePluginState.isProactiveContextPanelOpen ?? false,
				showNewRecommendationsNotification:
					proactivePluginState.showNewRecommendationsNotification ?? false,
				unreadRecommendationCount: getUnreadRecommendationsCount(proactivePluginState),
				decorationSet: proactivePluginState.decorationSet,
				analyticsAIInteractionId: proactivePluginState.analyticsAIInteractionId,
			};
		},

		contentComponent: ({ editorView, wrapperElement, appearance, providerFactory }) => {
			if (!editorView) {
				return null;
			}

			return (
				<ContentComponentWrapper
					api={api}
					editorView={editorView}
					wrapperElement={wrapperElement}
					appearance={appearance}
					config={config}
					providerFactory={providerFactory}
				/>
			);
		},

		pluginsOptions: fg('platform_editor_ai_object_sidebar_injection')
			? {}
			: {
					contextPanel: getContextPanel(config, () => editorViewRef.current)(api),
				},
	};
};
